/**
 * EvidenceStep
 *
 * Renders the session file tray and the "Attach selected / Skip" action bar.
 * Shown between Review & Submit and Result (single-activity flow) or between
 * Review & Submit and the flow exit (asset flow). Users can attach one or
 * more staged files to the just-created activities, or skip.
 *
 * The attach layer is plural: `activityIds: string[]` always. Single-activity
 * callers pass `[id]`. Multi-activity (asset flow with N activities)
 * defaults to "attach each selected file to every activity" and lets the
 * user narrow per-row via the target-activity picker in SessionFileTray.
 *
 * Dedup happens server-side: promoting the same sha256 twice reuses the
 * existing evidence record and links the new activities to it without
 * re-copying bytes.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';

import { SessionFileTray } from '../SessionFileTray';
import {
  type PromoteSelectedFailure,
  type PromoteSelectedResult,
  type UseUploadSessionResult,
} from '../../hooks/useUploadSession';
import type {
  EmissionEvidence,
  SessionFileMetadataUpdate,
  UploadSession,
} from '../../types/emissions';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { getErrorMessage } from '@utils/error';
import { formatExpiresIn, formatTimeAgo } from '../../../../utils/date';

type AttachFailure = Extract<PromoteSelectedFailure, { kind: 'attach' }>;

/**
 * Selection snapshot captured when `deferAttach` is true and the user clicks
 * "Next →". The parent flow uses this to promote-and-attach at a later step
 * (e.g. the asset wizard's Review+Submit), once real activity IDs exist.
 */
export interface DeferredAttachSelection {
  selectedFileIds: string[];
  /** Per-file override of target activity ids (subset of activityIds). */
  targetActivitySelections: Record<string, string[]>;
}

interface EvidenceStepProps {
  initiativeId: string;
  /**
   * Activities the current flow just created (or is editing). Empty array
   * means promote-only (library upload with no attach); single-element means
   * the single-activity flow; multi-element is the asset flow. In
   * `deferAttach` mode these may be placeholder ids (e.g. `slot:0`) that the
   * parent translates to real ids at submit time.
   */
  activityIds: string[];
  /**
   * Optional human-readable labels for the per-file target-activity picker.
   * Keyed by activity id. When omitted, the picker falls back to id suffixes.
   */
  activityLabels?: Record<string, string>;
  /**
   * When true, the step runs as a "stage, don't commit" screen: the primary
   * button reads "Next →" and clicking it invokes `onDeferredContinue` with
   * the current selection instead of calling promoteSelected. Files still
   * upload to the session on drop — only the promote/attach is deferred.
   * Used by the asset wizard so evidence can be associated before the
   * asset+activities are actually created server-side.
   */
  deferAttach?: boolean;
  /**
   * Fires on Skip OR on successful attach (when `deferAttach` is false).
   * The evidences argument carries the list just linked — passed through
   * so the result step can echo them back to the user as confirmation.
   * Empty on Skip.
   */
  onContinue: (attachedEvidences?: EmissionEvidence[]) => void;
  /**
   * Fires when `deferAttach` is true and the user clicks "Next →". Carries
   * the file + routing selection so the parent can promote later. Required
   * iff `deferAttach` is set.
   */
  onDeferredContinue?: (selection: DeferredAttachSelection) => void;
  /**
   * Optional — if provided, the parent can use the session the step created.
   * Useful when the tray is reused across multiple activity creations.
   */
  onSessionChange?: (session: UploadSession | null) => void;
  /**
   * The parent owns a single `useUploadSession` instance and passes the
   * whole hook result in. Required (not optional) — the step previously
   * fell back to an internal `useUploadSession` call when omitted, but that
   * created a second parallel hook instance with its own state that
   * couldn't be reset via the override's `startFreshSession`. Every caller
   * in this repo already passes it.
   */
  uploadSession: UseUploadSessionResult;
  /**
   * Optional — when provided, a "Back" button renders next to Skip / Attach
   * inside the card footer. Used by the multi-add Evidence step where the
   * wizard's external Back button would otherwise sit outside the card.
   */
  onBack?: () => void;
}

export const EvidenceStep = ({
  activityIds,
  activityLabels,
  deferAttach = false,
  onContinue,
  onDeferredContinue,
  onSessionChange,
  uploadSession,
  onBack,
}: EvidenceStepProps) => {
  const {
    session,
    isCreating,
    ensureSession,
    startFreshSession,
    uploadFile,
    updateMetadata,
    removeFile,
    promoteSelected,
    retryAttach,
  } = uploadSession;

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pendingAttach, setPendingAttach] = useState<AttachFailure[]>([]);
  const [isAttaching, setIsAttaching] = useState(false);
  // Per-file target activity narrowing. Entries mean "this file links to a
  // subset of activityIds instead of all of them". Missing entries default
  // to the full activityIds list. Reset when the selection or activityIds
  // change so the UI can't stash stale narrowings.
  const [targetActivitySelections, setTargetActivitySelections] = useState<
    Record<string, string[]>
  >({});

  const unpromotedFiles = useMemo(
    () => (session?.files ?? []).filter((f) => !f.promotedToEvidenceId),
    [session]
  );
  const promotedCount = (session?.files.length ?? 0) - unpromotedFiles.length;
  const unpromotedIdsKey = unpromotedFiles.map((f) => f.fileId).sort().join(',');

  // Diff-merge selection across file-list changes: newly-added files default
  // to selected, removed files drop out, and — critically — files the user
  // has explicitly unchecked stay unchecked when another file is uploaded
  // or removed. Previously this effect reset selection to "all unpromoted",
  // clobbering the user's unchecks on every list change.
  const prevUnpromotedIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const currentIds = new Set(unpromotedIdsKey ? unpromotedIdsKey.split(',') : []);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of prev) {
        if (!currentIds.has(id)) {
          next.delete(id);
        }
      }
      for (const id of currentIds) {
        if (!prevUnpromotedIdsRef.current.has(id)) {
          next.add(id);
        }
      }
      return next;
    });
    prevUnpromotedIdsRef.current = currentIds;
  }, [unpromotedIdsKey]);

  useEffect(() => {
    onSessionChange?.(session);
  }, [onSessionChange, session]);

  const canAttach = activityIds.length > 0;
  const hasPending =
    (selectedIds.size > 0 && (session?.files ?? []).length > 0) || pendingAttach.length > 0;
  const selectionReady = canAttach && hasPending;

  // Promote 400s with "evidenceType is required before promotion" if any file
  // lacks a type. Block the submit in the UI so the error never reaches the
  // wire — cheaper than showing a toast after the fact, and removes the
  // "nothing happened" feeling when the server rejects silently.
  const selectedMissingType = useMemo(
    () =>
      (session?.files ?? []).filter(
        (f) => selectedIds.has(f.fileId) && !f.metadata.evidenceType,
      ),
    [selectedIds, session?.files],
  );
  const hasMissingType = selectedMissingType.length > 0;

  // Reject an empty target scope (user unticked every activity for a file).
  // Otherwise we'd promote without attaching — confusing silent partial.
  const filesWithEmptyTarget = useMemo(
    () =>
      Object.entries(targetActivitySelections).filter(
        ([fileId, targets]) => selectedIds.has(fileId) && targets.length === 0,
      ),
    [selectedIds, targetActivitySelections],
  );
  const hasEmptyTarget = filesWithEmptyTarget.length > 0;

  const handleUpload = async (file: File, metadata?: SessionFileMetadataUpdate) => {
    await ensureSession();
    return uploadFile(file, metadata);
  };

  const handleAttach = async () => {
    // Deferred mode: parent flow (e.g. asset wizard) attaches at a later
    // step once real activity IDs exist. Just capture the selection.
    if (deferAttach) {
      onDeferredContinue?.({
        selectedFileIds: [...selectedIds],
        targetActivitySelections,
      });
      return;
    }

    if (!canAttach || (selectedIds.size === 0 && pendingAttach.length === 0)) {
      onContinue();
      return;
    }

    setIsAttaching(true);
    // Retry attach-only failures first — their files are already promoted,
    // so feeding them through promoteSelected would throw "Cannot modify a
    // promoted file" on the server.
    const attachLeftovers = pendingAttach.length ? await retryAttach(pendingAttach) : [];

    // Group the selected files by their target-activity signature and run
    // each group through its own promoteSelected call. The default group
    // (no override) gets all activityIds; narrowed files get their subset.
    const grouped = groupByTargetActivities(
      [...selectedIds],
      targetActivitySelections,
      activityIds,
    );
    const groupResults = await Promise.all(
      grouped.map(({ fileIds, targetIds }) =>
        promoteSelected(fileIds, { activityIds: targetIds }),
      ),
    );
    const { evidences, failures } = mergeResults(groupResults);
    setIsAttaching(false);

    const promoteFailures = failures.filter((f): f is Extract<PromoteSelectedFailure, { kind: 'promote' }> => f.kind === 'promote');
    const newAttachFailures = failures.filter((f): f is AttachFailure => f.kind === 'attach');
    const stillAttachFailing = [...attachLeftovers, ...newAttachFailures];

    setSelectedIds(new Set(promoteFailures.map((f) => f.fileId)));
    setPendingAttach(stillAttachFailing);

    if (promoteFailures.length === 0 && stillAttachFailing.length === 0) {
      // Surface the just-attached evidences to the caller so the result
      // step can echo them back. Filter by evidenceId rather than fileId
      // because the same file in two groups won't happen (a file belongs to
      // exactly one targeting group), but belt-and-braces.
      const failedIds = new Set(newAttachFailures.map((f) => f.evidenceId));
      const attached = evidences.filter((ev) => !failedIds.has(ev._id));
      onContinue(attached);
      return;
    }

    // Count (file, activity) links, not evidences. In the asset flow one
    // evidence fans out to N activities, so a single attach failure is not
    // a whole-evidence failure — and a single promote failure is N link
    // failures (the file didn't make it to any of its target activities).
    // The old `evidences.length - newAttachFailures.length` arithmetic
    // could go negative for multi-activity batches.
    const totalLinks = grouped.reduce(
      (acc, g) => acc + g.fileIds.length * g.targetIds.length,
      pendingAttach.length,
    );
    const promoteFailedLinks = grouped.reduce((acc, g) => {
      const promoteFailuresInGroup = g.fileIds.filter((id) =>
        promoteFailures.some((pf) => pf.fileId === id),
      ).length;
      return acc + promoteFailuresInGroup * g.targetIds.length;
    }, 0);
    const failedCount = promoteFailedLinks + stillAttachFailing.length;
    const attachedCount = totalLinks - failedCount;
    // Dedupe distinct error messages across promote + attach failures so an
    // N-link batch surfaces the actual reasons, not just whatever happened
    // to be at index 0. Cap at 3 to keep the toast legible; "+N more"
    // signals there's additional detail for the user to chase.
    const distinctErrors = [
      ...new Set(
        [
          ...promoteFailures.map((f) => getErrorMessage(f.error)),
          ...stillAttachFailing.map((f) => getErrorMessage(f.error)),
        ].filter((m) => m.length > 0),
      ),
    ];
    const head = distinctErrors.slice(0, 3).join('; ');
    const tail = distinctErrors.length > 3 ? ` (+${distinctErrors.length - 3} more)` : '';
    const detail = head ? ` — ${head}${tail}` : '';
    generateToast({
      color: 'danger',
      title: 'Some evidence did not attach',
      message: `${attachedCount} attached, ${failedCount} failed${detail}. Review and retry, or Skip to continue.`,
    });
  };

  // In deferAttach mode, the button is a "Next" navigation — it's allowed
  // even when no files are selected (user can skip ahead) and even if
  // activityIds is empty at this moment (asset wizard doesn't have real
  // IDs yet; the button still advances to review). Only type/target
  // validation blocks the advance, since those errors are structural and
  // would bite at the deferred promote.
  const submitDisabled = computeSubmitDisabled({
    deferAttach,
    hasMissingType,
    hasEmptyTarget,
    isAttaching,
    selectionReady,
    canAttach,
  });
  const submitLabel = computeSubmitLabel({ deferAttach, isAttaching });

  return (
    <Card>
      <CardContent className='tw:p-4 tw:space-y-4'>
        <div>
          <h5 className='tw:font-semibold tw:mb-1'>Attach evidence</h5>
          <p className='tw:text-sm tw:text-ThemeTextMedium tw:mb-0'>
            Drop invoices, meter readings, certificates, or other supporting documents.
            Files stay available for 7 days and can back multiple activities.
          </p>
        </div>

        <SessionFileTray
          session={session}
          onUpload={handleUpload}
          onRemove={removeFile}
          onUpdateMetadata={updateMetadata}
          selectedFileIds={selectedIds}
          onSelectionChange={setSelectedIds}
          selectable
          isCreatingSession={isCreating}
          activityIds={activityIds}
          activityLabels={activityLabels}
          targetActivitySelections={targetActivitySelections}
          onTargetActivitySelectionsChange={setTargetActivitySelections}
          fileListHeader={
            session && session.files.length > 0 ? (
              <div className='tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2 tw:text-xs tw:text-ThemeTextMedium'>
                <div>
                  Session started {formatTimeAgo(session.created)} ·{' '}
                  {formatExpiresIn(session.expiresAt)}
                  {promotedCount > 0 ? (
                    <>
                      {' · '}
                      <span>{promotedCount} attached earlier</span>
                    </>
                  ) : null}
                </div>
                {unpromotedFiles.length > 0 ? (
                  <Button
                    variant='link'
                    size='sm'
                    onClick={() => {
                      startFreshSession().catch((error) => {
                        toastFromError({ error, title: 'Could not discard session' });
                      });
                    }}
                    disabled={isAttaching}
                  >
                    Start fresh
                  </Button>
                ) : null}
              </div>
            ) : null
          }
        />

        <div className='tw:flex tw:items-center tw:justify-end tw:gap-2 tw:pt-2'>
          {hasMissingType ? (
            <div className='tw:flex-1 tw:text-xs tw:text-ThemeAccentDanger'>
              Set a type on {selectedMissingType.length} selected file
              {selectedMissingType.length === 1 ? '' : 's'} before attaching.
            </div>
          ) : hasEmptyTarget ? (
            <div className='tw:flex-1 tw:text-xs tw:text-ThemeAccentDanger'>
              {filesWithEmptyTarget.length} file
              {filesWithEmptyTarget.length === 1 ? ' has' : 's have'} no target activity.
              Pick at least one.
            </div>
          ) : null}
          {onBack && (
            <Button variant='outline' onClick={onBack} disabled={isAttaching}>
              <ArrowLeft className='tw:size-4' />
              Back
            </Button>
          )}
          <Button variant='ghost' onClick={() => onContinue()} disabled={isAttaching}>
            Skip — add later
          </Button>
          <Button onClick={handleAttach} disabled={submitDisabled}>
            {submitLabel}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Bucket selected fileIds by their target-activity signature. Files without
 * an explicit narrowing fall into the "all activityIds" default bucket.
 * Returns at most one bucket per distinct `targetIds` tuple so we issue the
 * minimum number of `promoteSelected` calls.
 */
function groupByTargetActivities(
  selectedFileIds: string[],
  overrides: Record<string, string[]>,
  defaultActivityIds: string[],
): Array<{ fileIds: string[]; targetIds: string[] }> {
  const buckets = new Map<string, { fileIds: string[]; targetIds: string[] }>();
  for (const fileId of selectedFileIds) {
    const narrowed = overrides[fileId];
    const targetIds = narrowed && narrowed.length > 0 ? narrowed : defaultActivityIds;
    const key = [...targetIds].sort().join(',');
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.fileIds.push(fileId);
    } else {
      buckets.set(key, { fileIds: [fileId], targetIds });
    }
  }
  return [...buckets.values()];
}

function mergeResults(parts: PromoteSelectedResult[]): PromoteSelectedResult {
  const evidences: EmissionEvidence[] = [];
  const failures: PromoteSelectedFailure[] = [];
  for (const part of parts) {
    evidences.push(...part.evidences);
    failures.push(...part.failures);
  }
  return { evidences, failures };
}

function computeSubmitDisabled({
  deferAttach,
  hasMissingType,
  hasEmptyTarget,
  isAttaching,
  selectionReady,
  canAttach,
}: {
  deferAttach: boolean;
  hasMissingType: boolean;
  hasEmptyTarget: boolean;
  isAttaching: boolean;
  selectionReady: boolean;
  canAttach: boolean;
}): boolean {
  if (hasMissingType || hasEmptyTarget || isAttaching) {
    return true;
  }
  if (deferAttach) {
    return false;
  }
  return !selectionReady || !canAttach;
}

function computeSubmitLabel({
  deferAttach,
  isAttaching,
}: {
  deferAttach: boolean;
  isAttaching: boolean;
}): string {
  if (deferAttach) {
    return 'Next';
  }
  if (isAttaching) {
    return 'Attaching…';
  }
  return 'Attach selected';
}
